import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Periode } from "@/types";
import { BookOpenCheck, Calendar, Clock } from "lucide-react";
import { MdBlock } from "react-icons/md";

// --- HELPER FUNCTION ---
const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  };
  return new Date(dateString).toLocaleDateString("id-ID", options);
};

// --- DUMMY DATA ---
const dummyPeriode: Partial<Periode>[] = [
  {
    id_sys_periode: "P-0010",
    nama_jalur_jenis: "Program Pascasarjana Gelmbang 2",
    tahun: "2026",
    waktu_mulai_daftar: "2026-03-03T00:00:00Z",
    waktu_selesai_daftar: "2026-04-21T23:59:59Z",
    waktu_pengumuman: "",
    keterangan:
      "Penerimaan Mahasiswa Baru Program Pascasarjana untuk Kerjasama TA Genap 2025/2026",
  },
  {
    id_sys_periode: "P-001",
    nama_jalur_jenis: "Seleksi Nasional Berdasarkan Prestasi (SNBP)",
    tahun: "2026",
    waktu_mulai_daftar: "2026-02-03T00:00:00Z", // 3 Februari 2026 [cite: 40]
    waktu_selesai_daftar: "2026-02-18T23:59:59Z", // 18 Februari 2026 [cite: 40]
    waktu_pengumuman: "2026-03-31T00:00:00Z", // 31 Maret 2026 [cite: 40]
    keterangan:
      "Jalur seleksi nasional. Pengumuman Hasil SNBP dijadwalkan pada 31 Maret 2026[cite: 40]. Pendaftaran berakhir pada 18 Februari 2026[cite: 40].",
  },
  {
    id_sys_periode: "P-002",
    nama_jalur_jenis: "Seleksi Nasional Berdasarkan Tes (SNBT)",
    tahun: "2026",
    waktu_mulai_daftar: "2026-03-25T00:00:00Z", // 25 Maret 2026 [cite: 40]
    waktu_selesai_daftar: "2026-04-07T23:59:59Z", // 7 April 2026 [cite: 40]
    waktu_pengumuman: "2026-05-25T00:00:00Z", // 25 Mei 2026 [cite: 40]
    tanggal_cbt: [
      "2026-04-21T00:00:00Z",
      "2026-04-30T23:59:59Z", // Pelaksanaan UTBK 21-30 April 2026 [cite: 40]
    ],
    keterangan:
      "Seleksi berbasis tes UTBK-SNBT. Pembayaran biaya UTBK dilakukan pada 25 Maret – 08 April 2026[cite: 40]. Pengumuman pada 25 Mei 2026[cite: 40].",
  },
  {
    id_sys_periode: "P-003",
    nama_jalur_jenis: "Mandiri CBT (Luring / Berbasis Domisili)",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-02T00:00:00Z", // 2 April 2026 [cite: 43]
    waktu_selesai_daftar: "2026-06-02T23:59:59Z", // 2 Juni 2026 [cite: 43]
    waktu_pengumuman: "2026-06-25T00:00:00Z", // 25 Juni 2026 [cite: 47]
    waktu_mulai_daftarkembali: "2026-07-01T00:00:00Z", // 1 Juli 2026 [cite: 51]
    waktu_selesai_daftarkembali: "2026-07-11T23:59:59Z", // 11 Juli 2026 [cite: 51]
    waktu_mulai_sanggah_kelulusan: "2026-06-29T00:00:00Z", // 29 Juni 2026 [cite: 49]
    waktu_selesai_sanggah_kelulusan: "2026-07-03T23:59:59Z", // 3 Juli 2026 [cite: 49]
    tanggal_cbt: [
      "2026-06-08T00:00:00Z",
      "2026-06-12T23:59:59Z", // UTBK Mandiri 8-12 Juni 2026 [cite: 45]
    ],
    keterangan:
      "Ujian Tulis Berbantuan Komputer bisa memilih luring di kampus Undiksha atau berbasis Domisili[cite: 27]. UTBK berbasis Domisili tidak bisa diikuti oleh: prodi kedokteran, S1 Kebidanan, S1 Keperawatan[cite: 28]. Pengumuman pada 25 Juni 2026[cite: 47].",
  },

  {
    id_sys_periode: "P-008",
    nama_jalur_jenis: "Mandiri - Menggunakan Skor UTBK SNBT",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-13T00:00:00Z", // [cite: 54]
    waktu_selesai_daftar: "2026-06-30T23:59:59Z", // [cite: 54]
    waktu_pengumuman: "2026-07-17T00:00:00Z", // [cite: 58]
    waktu_mulai_daftarkembali: "2026-07-22T00:00:00Z", // [cite: 62]
    waktu_selesai_daftarkembali: "2026-07-31T23:59:59Z", // [cite: 62]
    keterangan:
      "Jalur seleksi mandiri dengan menggunakan nilai atau skor UTBK sebagai acuan penilaian[cite: 17, 52].",
  },

  // --- PEMECAHAN JALUR MANDIRI NON-CBT ---
  // (Talent Scouting, Raport, Kepemimpinan, SMA Lab, Skor UTBK menggunakan linimasa yang seragam)
  {
    id_sys_periode: "P-004",
    nama_jalur_jenis: "Mandiri - Talent Scouting",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-02T00:00:00Z", // 2 April 2026 [cite: 54]
    waktu_selesai_daftar: "2026-06-30T23:59:59Z", // 30 Juni 2026 [cite: 54]
    waktu_pengumuman: "2026-07-17T00:00:00Z", // 17 Juli 2026 [cite: 58]
    waktu_mulai_daftarkembali: "2026-07-22T00:00:00Z", // 22 Juli 2026 [cite: 62]
    waktu_selesai_daftarkembali: "2026-07-31T23:59:59Z", // 31 Juli 2026 [cite: 62]
    waktu_mulai_sanggah_kelulusan: "2026-07-20T00:00:00Z", // 20 Juli 2026 [cite: 60]
    waktu_selesai_sanggah_kelulusan: "2026-07-24T23:59:59Z", // 24 Juli 2026 [cite: 60]
    tanggal_cbt: [
      "2026-07-06T00:00:00Z",
      "2026-07-10T23:59:59Z", // Tes Wawancara 6-10 Juli 2026 [cite: 56]
    ],
    keterangan:
      "Penerimaan mahasiswa melalui jalur prestasi dalam bidang non-akademik, seperti: prestasi olahraga, seni, dan bidang minat khusus[cite: 24]. Tes dilakukan dengan wawancara[cite: 26]. Tidak dibuka untuk prodi kedokteran, S1 Kebidanan, S1 Keperawatan[cite: 25].",
  },
  {
    id_sys_periode: "P-005",
    nama_jalur_jenis: "Mandiri - Jalur Raport",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-02T00:00:00Z", // [cite: 54]
    waktu_selesai_daftar: "2026-06-30T23:59:59Z", // [cite: 54]
    waktu_pengumuman: "2026-07-17T00:00:00Z", // [cite: 58]
    waktu_mulai_daftarkembali: "2026-07-22T00:00:00Z", // [cite: 62]
    waktu_selesai_daftarkembali: "2026-07-31T23:59:59Z", // [cite: 62]
    keterangan:
      "Mekanisme seleksi sama dengan SNBP menggunakan nilai raport dan prestasi akademik dan non-akademik[cite: 33, 34]. Tidak dibuka untuk prodi Kedokteran, S1 Kebidanan, S1 Keperawatan[cite: 35].",
  },
  {
    id_sys_periode: "P-006",
    nama_jalur_jenis: "Mandiri - Jalur Khusus Kepemimpinan",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-02T00:00:00Z", // [cite: 54]
    waktu_selesai_daftar: "2026-06-30T23:59:59Z", // [cite: 54]
    waktu_pengumuman: "2026-07-17T00:00:00Z", // [cite: 58]
    waktu_mulai_daftarkembali: "2026-07-22T00:00:00Z", // [cite: 62]
    waktu_selesai_daftarkembali: "2026-07-31T23:59:59Z", // [cite: 62]
    tanggal_cbt: [],
    keterangan:
      "Diperuntukkan khusus bagi siswa yang memiliki pengalaman kepemimpinan, seperti Pengurus OSIS[cite: 16]. Calon mahasiswa akan mengikuti tahapan Tes Wawancara[cite: 55].",
  },
  {
    id_sys_periode: "P-007",
    nama_jalur_jenis: "Mandiri - Lulusan SMA Laboratorium",
    tahun: "2026",
    waktu_mulai_daftar: "2026-04-02T00:00:00Z", // [cite: 54]
    waktu_selesai_daftar: "2026-06-30T23:59:59Z", // [cite: 54]
    waktu_pengumuman: "2026-07-17T00:00:00Z", // [cite: 58]
    waktu_mulai_daftarkembali: "2026-07-22T00:00:00Z", // [cite: 62]
    waktu_selesai_daftarkembali: "2026-07-31T23:59:59Z", // [cite: 62]
    keterangan:
      "Jalur penerimaan khusus bagi siswa lulusan SMA Laboratorium Undiksha[cite: 18, 52].",
  },
];
// --- COMPONENT ---
export default function Upcoming() {
  const today = new Date();

  // Memfilter hanya yang belum buka (waktu mulai > hari ini)
  const upcomingPeriode = dummyPeriode.filter((p) => {
    const start = new Date(p.waktu_mulai_daftar!);
    return today < start;
  });

  // Komponen Card Upcoming
  const UpcomingCard = ({ data }: { data: Partial<Periode> }) => (
    <div className="relative flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
      {/* Top Gradient Accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-400 to-purple-500 opacity-70 group-hover:opacity-100 transition-opacity" />

      {/* Card Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex justify-between items-start mb-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[11px] font-bold uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5" />
            Segera Hadir
          </span>
          <div className="flex gap-1.5">
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              {data.tahun}
            </span>
            {data.gelombang_show && (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                {data.gelombang_show}
              </span>
            )}
          </div>
        </div>

        <h3 className="text-xl font-extrabold text-slate-800 dark:text-slate-100 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {data.nama_jalur_jenis}
        </h3>
      </div>

      {/* Card Body - Menampilkan semua info kecuali Keterangan */}
      <div className="px-6 flex flex-col gap-3 flex-1">
        {/* Info Pendaftaran: Mulai & Selesai */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
          <div className="p-2 bg-white dark:bg-slate-700 rounded-lg shadow-sm shrink-0">
            <Calendar className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full">
            <div className="flex-1">
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider mb-0.5">
                Dibuka
              </p>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-none">
                {formatDate(data.waktu_mulai_daftar!)}
              </p>
            </div>

            {/* Divider Vertikal/Horizontal (responsive) */}
            <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700" />

            <div className="flex-1">
              <p className="text-[10px] text-red-500/80 dark:text-red-400/80 font-semibold uppercase tracking-wider mb-0.5">
                Ditutup
              </p>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-none">
                {formatDate(data.waktu_selesai_daftar!)}
              </p>
            </div>
          </div>
        </div>

        {/* Info Jadwal Tes/CBT (Hanya muncul jika ada) */}
        {data.tanggal_cbt && data.tanggal_cbt.length === 2 && (
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50">
            <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-md shrink-0">
              <BookOpenCheck className="w-4 h-4 text-indigo-500" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider">
                {["Mandiri - Talent Scouting"].includes(
                  data.nama_jalur_jenis || "",
                )
                  ? "Jadwal Wawancara"
                  : "Jadwal Ujian / Tes"}
              </p>
              <p className="text-xs font-bold text-slate-700 dark:text-slate-200 mt-0.5">
                {formatDate(data.tanggal_cbt[0])} -{" "}
                {formatDate(data.tanggal_cbt[1])}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Card Footer / Action */}
      <div className="p-6 pt-5 mt-auto">
        <Button
          disabled
          variant="outline"
          className="w-full bg-slate-50 dark:bg-slate-800/50 text-slate-500"
        >
          <MdBlock className="w-4 h-4 mr-2 opacity-70" />
          Belum Dibuka
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 md:p-6">
      {/* <div className="flex flex-col gap-1 mb-8">
        <h2 className="text-2xl font-extrabold text-slate-800 dark:text-slate-100">
          Jalur Pendaftaran Mendatang
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Persiapkan diri Anda. Berikut adalah jalur seleksi yang akan segera
          dibuka.
        </p>
      </div> */}

      <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
        <Badge
          variant="outline"
          className="border-blue-200 text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300 px-4 py-1 mb-2"
        >
          Tahun Akademik 2026/2027
        </Badge>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Jalur Pendaftaran Mendatang
        </h1>
        <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
          Persiapkan diri Anda. Berikut adalah jalur seleksi yang akan segera
          dibuka.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {upcomingPeriode.length > 0 ? (
          upcomingPeriode.map((periode) => (
            <UpcomingCard key={periode.id_sys_periode} data={periode} />
          ))
        ) : (
          <div className="col-span-full py-12 text-center rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
            <Clock className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 dark:text-slate-300 font-medium">
              Belum ada informasi jalur pendaftaran selanjutnya.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Silakan periksa halaman ini secara berkala.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
